import { createChunks } from "../util/array-extensions";
import {
  Firestore,
  Timestamp,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getCount,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore/lite";
import { firebaseDb } from "../firebase/client-app";
import { DateFields, DbBusinessClient, DbBusinessClientNote } from "./models";
import { R2Module } from "../r2/model";
import { r2Service } from "../r2/service";
import { axiosInstance } from "../rest/axios";
import { getCookie } from "cookies-next";
import { DbUser, Gender } from "../user/models";

export type clientFields = "birthday" | "anniversary";

export class ClientService {
  private clientsRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_clients",
    );
  }

  private clientNotesRef(
    firestore: Firestore,
    businessId: string,
    clientId: string,
  ) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_clients/" + clientId + "/notes",
    );
  }

  async getClientCounts(
    businessId: string,
    startDate: Timestamp | null,
    endDate: Timestamp | null,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    let clientsQuery = query(this.clientsRef(firestore, businessId));
    if (startDate) {
      clientsQuery = query(clientsQuery, where("created_at", ">=", startDate));
    }
    if (endDate) {
      clientsQuery = query(clientsQuery, where("created_at", "<=", endDate));
    }
    const clients = await getCount(clientsQuery);
    return clients.data().count;
  }

  async getBusinessClient(
    businessId: string,
    clientId: string,
    firestore: Firestore = firebaseDb,
  ) {
    const client = await getDoc(
      doc(this.clientsRef(firestore, businessId), clientId),
    );
    if (!client.exists()) {
      return null;
    }
    return client.data() as DbBusinessClient;
  }

  async getClientNotes(
    businessId: string,
    clientId: string,
    lastClientNote?: DbBusinessClientNote | null,
    pageSize: number = 10,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessClientNote[]> {
    let q = query(
      this.clientNotesRef(firestore, businessId, clientId),
      orderBy("created_at", "desc"),
      limit(pageSize),
    );
    if (lastClientNote) {
      q = query(q, startAfter(lastClientNote.created_at));
    }
    const notes = await getDocs(q);
    return notes.docs.map((doc) => doc.data()) as DbBusinessClientNote[];
  }

  async addClientNote(
    businessId: string,
    clientId: string,
    note: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessClientNote> {
    const noteId = doc(this.clientNotesRef(firestore, businessId, clientId)).id;
    const data: DbBusinessClientNote = {
      id: noteId,
      note: note,
      created_at: Timestamp.now(),
      updated_at: Timestamp.now(),
    };
    await setDoc(
      doc(this.clientNotesRef(firestore, businessId, clientId), noteId),
      data,
    );
    return data;
  }

  async updateClientNote(
    businessId: string,
    clientId: string,
    noteId: string,
    note: string,
    firestore: Firestore = firebaseDb,
  ) {
    const docRef = doc(
      this.clientNotesRef(firestore, businessId, clientId),
      noteId,
    );
    const data = {
      note,
      updated_at: Timestamp.now(),
    };
    await updateDoc(docRef, data);
  }

  async deleteClientNote(
    businessId: string,
    clientId: string,
    noteId: string,
    firestore: Firestore = firebaseDb,
  ) {
    const docRef = doc(
      this.clientNotesRef(firestore, businessId, clientId),
      noteId,
    );
    await deleteDoc(docRef);
  }

  async addBusinessClientImage(
    businessId: string,
    clientId: string,
    imageUrls: string[],
    firestore: Firestore = firebaseDb,
  ): Promise<string[]> {
    await updateDoc(doc(this.clientsRef(firestore, businessId), clientId), {
      photos: arrayUnion(...imageUrls),
    });
    return imageUrls;
  }

  async removeBusinessClientImage(
    businessId: string,
    clientId: string,
    image: string,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await r2Service.deleteFile(R2Module.businessClient, image, businessId);
    await updateDoc(doc(this.clientsRef(firestore, businessId), clientId), {
      photos: arrayRemove(image),
    });
  }

  async getBusinessClients(
    businessId: string,
    startAfterDate?: Timestamp,
    startDate?: Timestamp,
    endDate?: Timestamp,
    pageSize: number = 10,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessClient[]> {
    let clientQuery = query(this.clientsRef(firestore, businessId));
    if (startDate && endDate) {
      clientQuery = query(
        clientQuery,
        where("created_at", ">=", startDate),
        where("created_at", "<=", endDate),
      );
    }
    clientQuery = query(
      clientQuery,
      orderBy("created_at", "desc"),
      limit(pageSize),
    );
    if (startAfterDate) {
      clientQuery = query(clientQuery, startAfter(startAfterDate));
    }
    const clientDocs = await getDocs(clientQuery);
    return clientDocs.docs.map((doc) => doc.data() as DbBusinessClient);
  }

  async addClient(
    businessId: string,
    name: string,
    phone: string,
    email?: string,
    birthday?: DateFields,
    anniversary?: DateFields,
    note?: string,
    gender?: Gender,
  ): Promise<string> {
    const response = await axiosInstance.post(
      "/business/client",
      {
        business_id: businessId,
        name: name,
        phone: phone,
        email: email,
        birthday: birthday,
        anniversary: anniversary,
        note: note,
        gender: gender,
      },
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );
    return response.data.client_id;
  }

  async addClients(
    businessId: string,
    clients: Array<{
      name: string;
      phone: string;
      email?: string;
      birthday?: DateFields;
      anniversary?: DateFields;
      note?: string;
      gender?: Gender;
    }>,
  ): Promise<{ total: number; success: number; failed: number }> {
    const payload = clients.map((c) => ({
      business_id: businessId,
      name: c.name,
      phone: c.phone,
      email: c.email,
      birthday: c.birthday,
      anniversary: c.anniversary,
      note: c.note,
      gender: c.gender,
    }));
    const response = await axiosInstance.post("/business/client", payload, {
      headers: { Authorization: getCookie("Authorization")?.toString() },
    });
    return response.data as {
      total: number;
      success: number;
      failed: number;
    };
  }

  async updateClient(
    businessId: string,
    clientId: string,
    name: string,
    phone: string,
    email: string,
    gender?: Gender,
  ): Promise<void> {
    try {
      await axiosInstance.put(
        "/business/client",
        {
          business_id: businessId,
          client_id: clientId,
          name: name,
          phone: phone,
          email: email,
          gender: gender,
        },
        {
          headers: { Authorization: getCookie("Authorization")?.toString() },
        },
      );
    } catch (error: any) {
      throw error.response.data;
    }
  }

  async deleteClient(businessId: string, clientId: string): Promise<void> {
    await axiosInstance.delete("/business/client", {
      data: {
        business_id: businessId,
        client_id: clientId,
      },
      headers: { Authorization: getCookie("Authorization")?.toString() },
    });
  }

  async searchBusinessClients(
    businessId: string,
    search: string,
    pageSize: number = 10,
    startAfterName?: string | null,
    startAfterUserId?: string | null,
  ): Promise<DbBusinessClient[]> {
    const response = await axiosInstance.get(`/business/${businessId}/client`, {
      params: {
        query: search,
        limit: pageSize,
        ...(startAfterName ? { "start-after-name": startAfterName } : {}),
        ...(startAfterUserId
          ? { "start-after-user-id": startAfterUserId }
          : {}),
      },
      headers: {
        Authorization: getCookie("Authorization")?.toString(),
      },
    });
    return response.data as DbBusinessClient[];
  }

  async saveClientBirthdayOrAnniversary(
    businessId: string,
    clientId: string,
    field: clientFields,
    value: DateFields | null,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await setDoc(
      doc(this.clientsRef(firestore, businessId), clientId),
      { [field]: value },
      { merge: true },
    );
  }

  async getBusinessClientsCount(
    businessId: string,
    startDate: Timestamp | null,
    endDate: Timestamp,
    firestore: Firestore = firebaseDb,
  ): Promise<{ clients: string[]; date: number; count: number }[]> {
    let clientsQuery = query(
      this.clientsRef(firestore, businessId),
      where("created_at", "<=", endDate),
    );

    if (startDate) {
      clientsQuery = query(clientsQuery, where("created_at", ">=", startDate));
    }
    const clients = await getDocs(
      query(clientsQuery, orderBy("created_at", "asc")),
    );

    if (clients.empty) {
      return [];
    }

    const data = clients.docs.map((doc) => doc.data() as DbBusinessClient);

    const groupedData = data.reduce(
      (acc, client) => {
        const date = client.created_at.toDate().toDateString();
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(client.id);
        return acc;
      },
      {} as Record<string, string[]>,
    );

    return Object.entries(groupedData).map(([date, clients]) => ({
      clients: clients,
      date: new Date(date).getTime(),
      count: clients.length,
    }));
  }

  async setClientServiceReminder(
    businessId: string,
    clientId: string,
    reminderData: Pick<
      DbBusinessClient,
      "next_reminder_at" | "reminder_service_id" | "reminder_interval_days"
    >,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(
      doc(this.clientsRef(firestore, businessId), clientId),
      reminderData,
    );
  }

  async getAllBusinessClients(
    businessId: string,
    startDate?: Timestamp,
    endDate?: Timestamp,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessClient[]> {
    let clientQuery = query(this.clientsRef(firestore, businessId));
    if (startDate && endDate) {
      clientQuery = query(
        clientQuery,
        where("created_at", ">=", startDate),
        where("created_at", "<=", endDate),
      );
    }

    const clientDocs = await getDocs(clientQuery);
    return clientDocs.docs.map((doc) => doc.data() as DbBusinessClient);
  }

  async getBusinessClientsByIds(
    businessId: string,
    clientIds: string[],
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessClient[]> {
    if (clientIds.length === 0) return [];

    const chunks =
      clientIds.length > 30 ? createChunks(clientIds, 30) : [clientIds];

    const clientPromises = chunks.map(async (chunk) => {
      const clientsSnapshot = await getDocs(
        query(this.clientsRef(firestore, businessId), where("id", "in", chunk)),
      );
      return clientsSnapshot.docs.map((doc) => doc.data() as DbBusinessClient);
    });

    const clientsArrays = await Promise.all(clientPromises);
    return clientsArrays.flat();
  }

  async findBusinessClientByPhone(
    businessId: string,
    phone: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessClient | null> {
    const clientsQuery = query(
      this.clientsRef(firestore, businessId),
      where("phone", "==", phone),
      limit(1),
    );

    const clients = await getDocs(clientsQuery);
    if (clients.empty) {
      return null;
    }

    return clients.docs[0].data() as DbBusinessClient;
  }

  async getOrCreateBusinessClient(
    businessId: string,
    name: string,
    phone: string,
    email?: string,
  ): Promise<string> {
    const existingClientByPhone = await this.findBusinessClientByPhone(
      businessId,
      phone,
    );
    if (existingClientByPhone) {
      return existingClientByPhone.id;
    }

    return this.addClient(businessId, name, phone, email);
  }
}

export const convertUserToBusinessClient = (user: DbUser): DbBusinessClient => {
  return {
    id: user.id,
    user_name: user.user_name || "",
    name: user.user_name?.toLowerCase() || "",
    phone: user.phone,
    photos: [],
    email: user.email,
    profile_image_url: user.profile_image_url,
    created_at: Timestamp.fromDate(
      user.created_at ? new Date(user.created_at) : new Date(),
    ),
  };
};

export const clientService = new ClientService();
